import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Security PIN for admin access
const ADMIN_PIN = '2847';

const SecretAdmin = () => {
  const [stage, setStage] = useState('pin'); // 'pin' | 'verify' | 'dashboard'
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState('');
  const [userEmail, setUserEmail] = useState('');
  
  // Data states
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payoutsSummary, setPayoutsSummary] = useState(null);
  const [processingPayouts, setProcessingPayouts] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Verification states
  const [verificationQueue, setVerificationQueue] = useState([]);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [reviewingListing, setReviewingListing] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    // Check for existing valid session
    const savedToken = localStorage.getItem('admin_token');
    const savedEmail = localStorage.getItem('admin_email');
    const savedTime = localStorage.getItem('admin_login_time');
    
    // Session expires after 1 hour
    const isExpired = savedTime && (Date.now() - parseInt(savedTime)) > 3600000;
    
    if (savedToken && savedEmail === 'caspian.mtanos@gmail.com' && !isExpired) {
      setToken(savedToken);
      setUserEmail(savedEmail);
      setStage('dashboard');
      fetchAllData(savedToken);
    }
    setLoading(false);
  }, []);

  const fetchAllData = async (authToken) => {
    const headers = { 'Authorization': `Bearer ${authToken}` };
    
    try {
      const [usersRes, listingsRes, bookingsRes, payoutsRes, verifyRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers }).catch(() => ({ data: { users: [] } })),
        axios.get(`${API}/admin/listings`, { headers }).catch(() => ({ data: { listings: [] } })),
        axios.get(`${API}/admin/bookings`, { headers }).catch(() => ({ data: { bookings: [] } })),
        axios.get(`${API}/admin/payouts/summary`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/verification-queue`, { headers }).catch(() => ({ data: { listings: [] } }))
      ]);
      
      setUsers(usersRes.data.users || usersRes.data || []);
      setListings(listingsRes.data.listings || listingsRes.data || []);
      setBookings(bookingsRes.data.bookings || bookingsRes.data || []);
      setPayoutsSummary(payoutsRes.data);
      setVerificationQueue(verifyRes.data.listings || []);
    } catch (e) {
      console.error('Fetch error:', e);
    }
  };

  const handleCleanupOrphaned = async () => {
    if (!window.confirm('This will permanently delete all listings where the host account no longer exists. Continue?')) {
      return;
    }
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const res = await axios.post(`${API}/admin/cleanup-orphaned`, {}, { headers });
      alert(`Cleaned up ${res.data.deleted?.length || 0} orphaned listings`);
      fetchAllData(token);
    } catch (e) {
      alert('Cleanup failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('Permanently delete this listing?')) {
      return;
    }
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      await axios.delete(`${API}/admin/listings/${listingId}`, { headers });
      fetchAllData(token);
    } catch (e) {
      alert('Delete failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleProcessAllPayouts = async () => {
    if (!window.confirm('This will mark ALL pending payouts as PAID. Make sure you have completed the bank transfers first. Continue?')) {
      return;
    }
    setProcessingPayouts(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const res = await axios.post(`${API}/admin/payouts/process-all`, {}, { headers });
      alert(`Successfully processed ${res.data.processed} payouts totaling $${res.data.total_amount?.toFixed(2) || '0.00'}`);
      fetchAllData(token);
    } catch (e) {
      alert('Failed to process payouts: ' + (e.response?.data?.detail || e.message));
    } finally {
      setProcessingPayouts(false);
    }
  };

  const handleProcessSinglePayout = async (payoutId) => {
    if (!window.confirm('Mark this payout as PAID?')) {
      return;
    }
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      await axios.put(`${API}/admin/payouts/${payoutId}/process?action=mark_paid`, {}, { headers });
      alert('Payout marked as paid');
      fetchAllData(token);
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleBackfillPaymentSheet = async () => {
    if (!window.confirm('This will add all historical payments to the Google Sheets "Payment Transfers" sheet. Continue?')) {
      return;
    }
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const res = await axios.post(`${API}/admin/backfill-payment-sheet`, {}, { headers });
      alert(`Successfully backfilled ${res.data.transactions || 0} transactions, ${res.data.payouts || 0} payouts, and ${res.data.bookings_added || 0} bookings to Google Sheets`);
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleVerifyListing = async (listingId, action) => {
    setVerificationLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      const body = { action };
      if (action === 'reject') {
        if (!rejectionReason.trim()) {
          alert('Please provide a rejection reason');
          setVerificationLoading(false);
          return;
        }
        body.rejection_reason = rejectionReason;
      }
      await axios.put(`${API}/admin/listings/${listingId}/verify`, body, { headers });
      alert(action === 'approve' ? 'Listing approved and published!' : 'Listing rejected. Host has been notified via email.');
      setReviewingListing(null);
      setRejectionReason('');
      fetchAllData(token);
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setVerificationLoading(false);
    }
  };

  const viewDocument = async (storagePath) => {
    try {
      const resp = await axios.get(`${API}/files/${storagePath}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      const blobUrl = URL.createObjectURL(resp.data);
      window.open(blobUrl, '_blank');
    } catch (e) {
      alert('Failed to load document: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pin === ADMIN_PIN) {
      setStage('verify');
      setError('');
    } else {
      setError('Invalid PIN');
      setPin('');
    }
  };

  const handleVerifySession = async () => {
    const mainToken = localStorage.getItem('session_token');
    if (!mainToken) {
      setError('No session found. Please login on SpotFinder first, then return here.');
      return;
    }

    try {
      const meRes = await axios.get(`${API}/auth/me`, { 
        headers: { 'Authorization': `Bearer ${mainToken}` }
      });
      
      if (meRes.data.email === 'caspian.mtanos@gmail.com') {
        localStorage.setItem('admin_token', mainToken);
        localStorage.setItem('admin_email', meRes.data.email);
        localStorage.setItem('admin_login_time', Date.now().toString());
        setToken(mainToken);
        setUserEmail(meRes.data.email);
        setStage('dashboard');
        fetchAllData(mainToken);
      } else {
        setError('Access denied. Admin only.');
      }
    } catch (e) {
      setError('Session invalid. Please login on SpotFinder first.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    localStorage.removeItem('admin_login_time');
    setStage('pin');
    setPin('');
    setToken('');
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    totalUsers: users.length,
    hosts: users.filter(u => u.role === 'host').length,
    drivers: users.filter(u => u.role === 'driver').length,
    admins: users.filter(u => u.role === 'admin').length,
    totalListings: listings.length,
    activeListings: listings.filter(l => l.is_available !== false).length,
    totalBookings: bookings.length,
    confirmedBookings: bookings.filter(b => b.status === 'confirmed').length,
    totalRevenue: bookings.reduce((sum, b) => sum + (b.total_price || 0), 0)
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent"></div>
      </div>
    );
  }

  // PIN Entry Stage
  if (stage === 'pin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-8 w-full max-w-md shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800">SpotFinder Admin</h1>
            <p className="text-gray-500 mt-2">Enter your security PIN</p>
          </div>

          {error && (
            <div className="bg-red-100 text-red-600 px-4 py-3 rounded-xl mb-6 text-center font-medium">
              {error}
            </div>
          )}

          <form onSubmit={handlePinSubmit}>
            <div className="flex justify-center gap-3 mb-8">
              {[0, 1, 2, 3].map((i) => (
                <input
                  key={i}
                  type="password"
                  maxLength={1}
                  className="w-14 h-14 text-center text-2xl font-bold border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none transition-all"
                  value={pin[i] || ''}
                  onChange={(e) => {
                    const newPin = pin.split('');
                    newPin[i] = e.target.value;
                    setPin(newPin.join(''));
                    if (e.target.value && e.target.nextSibling) {
                      e.target.nextSibling.focus();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && !pin[i] && e.target.previousSibling) {
                      e.target.previousSibling.focus();
                    }
                  }}
                />
              ))}
            </div>
            <button
              type="submit"
              className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all shadow-lg hover:shadow-xl"
            >
              Continue
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Verify Session Stage
  if (stage === 'verify') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-8 w-full max-w-md shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Verify Identity</h1>
            <p className="text-gray-500 mt-2">Confirm your SpotFinder session</p>
          </div>

          {error && (
            <div className="bg-red-100 text-red-600 px-4 py-3 rounded-xl mb-6 text-center font-medium">
              {error}
            </div>
          )}

          <button
            onClick={handleVerifySession}
            className="w-full py-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            </svg>
            Verify with Google Session
          </button>

          <button
            onClick={() => setStage('pin')}
            className="w-full mt-4 py-3 text-gray-500 hover:text-gray-700 font-medium transition-all"
          >
            ← Back
          </button>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">S</span>
              </div>
              <div>
                <h1 className="font-bold text-gray-800">SpotFinder</h1>
                <p className="text-xs text-gray-500">Admin Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">{userEmail}</span>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-red-500 hover:bg-red-50 rounded-lg font-medium transition-all"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            title="Total Users" 
            value={stats.totalUsers} 
            icon="👥" 
            color="from-blue-400 to-blue-600"
            subtitle={`${stats.hosts} hosts, ${stats.drivers} drivers`}
          />
          <StatCard 
            title="Listings" 
            value={stats.totalListings} 
            icon="🅿️" 
            color="from-emerald-400 to-emerald-600"
            subtitle={`${stats.activeListings} active`}
          />
          <StatCard 
            title="Bookings" 
            value={stats.totalBookings} 
            icon="📅" 
            color="from-amber-400 to-orange-500"
            subtitle={`${stats.confirmedBookings} confirmed`}
          />
          <StatCard 
            title="Revenue" 
            value={`$${stats.totalRevenue.toFixed(0)}`} 
            icon="💰" 
            color="from-pink-400 to-rose-500"
            subtitle="Total earnings"
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex">
              {[
                { id: 'overview', label: 'Overview', icon: '📊' },
                { id: 'verification', label: `Verify (${verificationQueue.length})`, icon: '🛡️' },
                { id: 'users', label: 'Users', icon: '👥' },
                { id: 'listings', label: 'Listings', icon: '🏠' },
                { id: 'bookings', label: 'Bookings', icon: '📅' },
                { id: 'payouts', label: 'Payouts', icon: '💳' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-4 px-6 font-medium transition-all flex items-center justify-center gap-2 ${
                    activeTab === tab.id 
                      ? 'text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50/50' 
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>{tab.icon}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Search */}
            {(activeTab === 'users' || activeTab === 'listings') && (
              <div className="mb-6">
                <input
                  type="text"
                  placeholder={`Search ${activeTab}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                />
              </div>
            )}

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <span>🆕</span> Recent Users
                  </h3>
                  <div className="space-y-3">
                    {users.slice(0, 5).map((user, i) => (
                      <UserCard key={i} user={user} onClick={() => setSelectedUser(user)} />
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <span>🏠</span> Recent Listings
                  </h3>
                  <div className="space-y-3">
                    {listings.slice(0, 5).map((listing, i) => (
                      <ListingCard key={i} listing={listing} />
                    ))}
                    {listings.length === 0 && (
                      <p className="text-gray-400 text-center py-8">No listings yet</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Verification Tab */}
            {activeTab === 'verification' && (
              <div>
                {/* Government Cross-Reference Tools */}
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <h4 className="font-semibold text-blue-800 mb-2">Government Verification Tools</h4>
                  <p className="text-sm text-blue-700 mb-3">Cross-reference host documents with official NSW government records:</p>
                  <div className="flex flex-wrap gap-3">
                    <a href="https://www.nswlrs.com.au/Land-Titles" target="_blank" rel="noopener noreferrer"
                      className="px-4 py-2 bg-white border border-blue-300 rounded-lg text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors">
                      NSW Land Registry Services (Title Search)
                    </a>
                    <a href="https://www.fairtrading.nsw.gov.au/housing-and-property/renting" target="_blank" rel="noopener noreferrer"
                      className="px-4 py-2 bg-white border border-blue-300 rounded-lg text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors">
                      NSW Fair Trading (Tenancy)
                    </a>
                    <a href="https://www.revenue.nsw.gov.au/taxes-duties-levies-royalties/land-tax" target="_blank" rel="noopener noreferrer"
                      className="px-4 py-2 bg-white border border-blue-300 rounded-lg text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors">
                      NSW Revenue (Land Tax Records)
                    </a>
                  </div>
                </div>

                {verificationQueue.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <p className="text-4xl mb-2">✅</p>
                    <p>No listings pending verification</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {verificationQueue.map((listing) => (
                      <div key={listing.listing_id} className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-gray-900">{listing.title}</h3>
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Pending Review</span>
                            </div>
                            <p className="text-sm text-gray-500">{listing.address}, {listing.suburb} {listing.postcode}</p>
                            <p className="text-sm text-gray-500 mt-1">
                              Host: <strong>{listing.host?.name}</strong> ({listing.host?.email})
                            </p>
                            {listing.verification_submitted_at && (
                              <p className="text-xs text-gray-400 mt-1">
                                Submitted: {new Date(listing.verification_submitted_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setReviewingListing(reviewingListing?.listing_id === listing.listing_id ? null : listing)}
                              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                            >
                              {reviewingListing?.listing_id === listing.listing_id ? 'Close' : 'Review Documents'}
                            </button>
                          </div>
                        </div>

                        {/* Expanded Review Panel */}
                        {reviewingListing?.listing_id === listing.listing_id && (
                          <div className="border-t border-gray-200 bg-gray-50 p-5">
                            {/* Documents */}
                            <h4 className="font-medium text-gray-800 mb-3">Uploaded Documents</h4>
                            {listing.documents?.length > 0 ? (
                              <div className="space-y-2 mb-4">
                                {listing.documents.map((doc) => (
                                  <div key={doc.doc_id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                                    <div>
                                      <p className="text-sm font-medium text-gray-800">{doc.original_filename}</p>
                                      <p className="text-xs text-gray-500">
                                        {doc.document_type === 'proof_of_ownership' ? 'Proof of Ownership' : 'Photo ID'} · {doc.content_type} · {new Date(doc.uploaded_at).toLocaleDateString()}
                                      </p>
                                    </div>
                                    <button
                                      onClick={() => viewDocument(doc.storage_path)}
                                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                                    >
                                      View Document
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-400 mb-4">No documents uploaded</p>
                            )}

                            {/* Verification Checklist */}
                            <div className="mb-4 p-3 bg-white border border-gray-200 rounded-lg">
                              <h5 className="font-medium text-gray-700 mb-2 text-sm">Verification Checklist</h5>
                              <ul className="space-y-1 text-sm text-gray-600">
                                <li>☐ Document clearly shows property address matching listing</li>
                                <li>☐ Document is a valid deed, lease, or tenancy agreement</li>
                                <li>☐ Name on document matches host account name ({listing.host?.name})</li>
                                <li>☐ If name differs, photo ID has been provided and matches</li>
                                <li>☐ Cross-referenced with NSW Land Registry (link above)</li>
                              </ul>
                            </div>

                            {/* Approve / Reject */}
                            <div className="flex flex-col sm:flex-row gap-3">
                              <button
                                onClick={() => handleVerifyListing(listing.listing_id, 'approve')}
                                disabled={verificationLoading}
                                className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
                              >
                                {verificationLoading ? 'Processing...' : 'Approve & Publish'}
                              </button>
                              <div className="flex-1 flex gap-2">
                                <input
                                  type="text"
                                  placeholder="Rejection reason (e.g. Document unreadable, address mismatch...)"
                                  value={rejectionReason}
                                  onChange={(e) => setRejectionReason(e.target.value)}
                                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                />
                                <button
                                  onClick={() => handleVerifyListing(listing.listing_id, 'reject')}
                                  disabled={verificationLoading || !rejectionReason.trim()}
                                  className="px-5 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                                >
                                  Reject
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-3">
                {filteredUsers.map((user, i) => (
                  <UserCard key={i} user={user} expanded onClick={() => setSelectedUser(user)} />
                ))}
                {filteredUsers.length === 0 && (
                  <p className="text-gray-400 text-center py-8">No users found</p>
                )}
              </div>
            )}

            {/* Listings Tab */}
            {activeTab === 'listings' && (
              <div>
                <div className="mb-4 flex justify-end">
                  <button
                    onClick={handleCleanupOrphaned}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all text-sm font-medium"
                  >
                    🧹 Cleanup Orphaned Listings
                  </button>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {listings.filter(l => 
                    l.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    l.address?.toLowerCase().includes(searchTerm.toLowerCase())
                  ).map((listing, i) => (
                    <ListingCard key={i} listing={listing} expanded onDelete={() => handleDeleteListing(listing.listing_id)} />
                  ))}
                  {listings.length === 0 && (
                    <p className="text-gray-400 text-center py-8 col-span-full">No listings yet</p>
                  )}
                </div>
              </div>
            )}

            {/* Bookings Tab */}
            {activeTab === 'bookings' && (
              <div className="space-y-3">
                {bookings.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No bookings yet</p>
                ) : (
                  bookings.map((booking, i) => (
                    <BookingCard key={i} booking={booking} />
                  ))
                )}
              </div>
            )}

            {/* Payouts Tab */}
            {activeTab === 'payouts' && (
              <div className="space-y-6">
                {/* Payout Stats */}
                {payoutsSummary?.summary && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                      <div className="text-amber-600 text-sm font-medium">Pending</div>
                      <div className="text-2xl font-bold text-amber-700">{payoutsSummary.summary.pending?.count || 0}</div>
                      <div className="text-sm text-amber-600">${payoutsSummary.summary.pending?.amount?.toFixed(2) || '0.00'}</div>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                      <div className="text-blue-600 text-sm font-medium">Processing</div>
                      <div className="text-2xl font-bold text-blue-700">{payoutsSummary.summary.processing?.count || 0}</div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                      <div className="text-green-600 text-sm font-medium">Paid</div>
                      <div className="text-2xl font-bold text-green-700">{payoutsSummary.summary.paid?.count || 0}</div>
                      <div className="text-sm text-green-600">${payoutsSummary.summary.paid?.amount?.toFixed(2) || '0.00'}</div>
                    </div>
                    <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                      <div className="text-red-600 text-sm font-medium">Failed</div>
                      <div className="text-2xl font-bold text-red-700">{payoutsSummary.summary.failed?.count || 0}</div>
                    </div>
                  </div>
                )}

                {/* Process All Button */}
                {payoutsSummary?.summary?.pending?.count > 0 && (
                  <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl p-6 border border-amber-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-amber-800 text-lg">
                          {payoutsSummary.summary.pending.count} Pending Payout{payoutsSummary.summary.pending.count !== 1 ? 's' : ''}
                        </h3>
                        <p className="text-amber-700">
                          Total: ${payoutsSummary.summary.pending.amount?.toFixed(2) || '0.00'} waiting to be processed
                        </p>
                      </div>
                      <button
                        onClick={handleProcessAllPayouts}
                        disabled={processingPayouts}
                        className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-semibold rounded-xl hover:opacity-90 transition-all shadow-lg disabled:opacity-50 flex items-center gap-2"
                      >
                        {processingPayouts ? (
                          <>
                            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Processing...
                          </>
                        ) : (
                          <>
                            ✓ Mark All as Paid
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Pending Payouts List */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-gray-800">Pending Payouts</h3>
                    <button
                      onClick={handleBackfillPaymentSheet}
                      className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-all flex items-center gap-2"
                    >
                      📊 Sync All to Sheets
                    </button>
                  </div>
                  {!payoutsSummary?.pending_payouts?.length ? (
                    <p className="text-gray-400 text-center py-8 bg-gray-50 rounded-xl">No pending payouts 🎉</p>
                  ) : (
                    <div className="space-y-3">
                      {payoutsSummary.pending_payouts.map((payout, i) => (
                        <div key={i} className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <span className="font-semibold text-gray-800">{payout.host_name}</span>
                              <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                                {payout.payout_method === 'bank_transfer' ? 'Bank Transfer' : 'Stripe'}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500">
                              {payout.listing_title} • {payout.host_email}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              Created: {payout.created_at ? format(new Date(payout.created_at), 'MMM d, yyyy h:mm a') : 'N/A'}
                            </div>
                          </div>
                          <div className="text-right flex items-center gap-4">
                            <div>
                              <div className="text-xl font-bold text-emerald-600">${payout.amount?.toFixed(2)}</div>
                              <div className="text-xs text-gray-400">{payout.payout_id?.slice(0, 20)}...</div>
                            </div>
                            <button
                              onClick={() => handleProcessSinglePayout(payout.payout_id)}
                              className="px-4 py-2 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 transition-all"
                            >
                              Mark Paid
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* User Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedUser(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {selectedUser.name?.charAt(0) || '?'}
              </div>
              <div>
                <h3 className="font-bold text-xl text-gray-800">{selectedUser.name}</h3>
                <p className="text-gray-500">{selectedUser.email}</p>
              </div>
            </div>
            <div className="space-y-3">
              <InfoRow label="Role" value={selectedUser.role} />
              <InfoRow label="Phone" value={selectedUser.phone || 'Not provided'} />
              <InfoRow label="Auth Method" value={selectedUser.auth_method || 'email'} />
              <InfoRow label="Early Adopter" value={selectedUser.is_early_adopter ? 'Yes ⭐' : 'No'} />
              <InfoRow label="Joined" value={selectedUser.created_at ? format(new Date(selectedUser.created_at), 'PPP') : 'Unknown'} />
            </div>
            <button
              onClick={() => setSelectedUser(null)}
              className="w-full mt-6 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-all"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Components
const StatCard = ({ title, value, icon, color, subtitle }) => (
  <div className={`bg-gradient-to-br ${color} rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105 cursor-default`}>
    <div className="flex justify-between items-start mb-3">
      <span className="text-3xl">{icon}</span>
    </div>
    <div className="text-3xl font-bold mb-1">{value}</div>
    <div className="text-sm opacity-90">{title}</div>
    {subtitle && <div className="text-xs opacity-75 mt-1">{subtitle}</div>}
  </div>
);

const UserCard = ({ user, expanded, onClick }) => (
  <div 
    onClick={onClick}
    className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-xl cursor-pointer transition-all group"
  >
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center text-white font-semibold">
        {user.name?.charAt(0) || '?'}
      </div>
      <div>
        <div className="font-medium text-gray-800 group-hover:text-emerald-600 transition-colors">{user.name || 'Unknown'}</div>
        <div className="text-sm text-gray-500">{user.email}</div>
        {expanded && user.phone && <div className="text-xs text-gray-400">{user.phone}</div>}
      </div>
    </div>
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
      user.role === 'admin' ? 'bg-purple-100 text-purple-700' :
      user.role === 'host' ? 'bg-emerald-100 text-emerald-700' :
      'bg-blue-100 text-blue-700'
    }`}>
      {user.role}
    </span>
  </div>
);

const ListingCard = ({ listing, expanded, onDelete }) => (
  <div className="p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all">
    <div className="flex justify-between items-start mb-1">
      <div className="font-medium text-gray-800">{listing.title}</div>
      {onDelete && (
        <button 
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="text-red-500 hover:bg-red-100 p-1 rounded transition-all"
          title="Delete listing"
        >
          🗑️
        </button>
      )}
    </div>
    <div className="text-sm text-gray-500 mb-1">{listing.address}</div>
    <div className="text-xs text-gray-400 mb-2">Host: {listing.host_id}</div>
    <div className="flex items-center justify-between">
      <span className="text-emerald-600 font-semibold">${listing.price_per_hour || listing.price_hourly || 0}/hr</span>
      {expanded && (
        <span className={`px-2 py-1 rounded-full text-xs ${
          listing.status === 'published' ? 'bg-green-100 text-green-700' : 
          listing.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
          'bg-gray-100 text-gray-600'
        }`}>
          {listing.status}
        </span>
      )}
    </div>
  </div>
);

const BookingCard = ({ booking }) => (
  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
    <div>
      <div className="font-medium text-gray-800">Booking #{booking.booking_id?.slice(-6) || 'N/A'}</div>
      <div className="text-sm text-gray-500">
        {booking.start_time ? format(new Date(booking.start_time), 'PPp') : 'Date N/A'}
      </div>
    </div>
    <div className="text-right">
      <div className="font-semibold text-emerald-600">${booking.total_price || 0}</div>
      <span className={`px-2 py-1 rounded-full text-xs ${
        booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
        booking.status === 'pending' ? 'bg-amber-100 text-amber-700' :
        'bg-gray-100 text-gray-600'
      }`}>
        {booking.status}
      </span>
    </div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-gray-100">
    <span className="text-gray-500">{label}</span>
    <span className="font-medium text-gray-800">{value}</span>
  </div>
);

export default SecretAdmin;
